/* eslint-disable no-restricted-globals */
import React, { useEffect } from 'react';
import { Row, Col, Form, message } from 'antd';
import { formatRut, validateRut } from '@abstract-cl/rut-ts';
import { Button, Input, Select } from '@cloudcar-app/cloudcar-components';
import { useHistory } from 'react-router-dom';
import { useMutation, useReactiveVar } from '@apollo/client';
import { concessionaireLogoUrl, concessionaireName } from 'apollo/cache';
import MessageError from 'utils/message.errors';
import rutFormValidator from 'utils/rutFormValidator';
import { InviteUserInput } from 'apollo/types';
import operations from 'apollo/operations';
import dealershipOptions from 'utils/kaufmann/dealershipMapper';
import { trimFormValues } from 'utils/trimFormValues';
import styles from './InviteUser.module.scss';

interface InviteUserFormValues {
  rut: string;
  email: string;
  name: string;
  dealer: string;
  firstName: string;
  concessionaireName: string;
}

const concessionaireOptions = [
  { value: 'Kaufmann', label: 'Kaufmann' },
  { value: 'AndesMotor', label: 'AndesMotor' },
];

const InviteUser: React.FC = () => {
  const history = useHistory();
  const [form] = Form.useForm();
  const logoUrl = useReactiveVar(concessionaireLogoUrl);
  const name = useReactiveVar(concessionaireName);

  const [inviteUser, { data, loading }] = useMutation<
    { inviteUser: string },
    { inviteUserInput: InviteUserInput }
  >(operations.INVITE_USER);

  const mapDealershipOptions = dealershipOptions.map((option) => ({
    label: option.label,
    value: option.value,
  }));

  const mapConcessionaireOptions = concessionaireOptions.map((option) => ({
    label: option.label,
    value: option.value,
  }));

  const handleChangeRut = (rut: string): string => {
    if (validateRut(rut)) {
      const formatted: string = formatRut(rut) || '';
      form.setFieldsValue({ rut: formatted });
      return rut;
    }
    form.setFieldsValue({
      rut: rut.split('.').join('').split('-').join(''),
    });
    return rut;
  };

  const sendForm = async (formValues: InviteUserFormValues) => {
    const values = trimFormValues(formValues);
    await inviteUser({
      variables: {
        inviteUserInput: {
          concessionaireName: name,
          ...(values.concessionaireName !== name && {
            extraConcessionaireName: values.concessionaireName,
          }),
          dealer: values.dealer,
          name: values.name,
          email: values.email,
          firstName: values.firstName,
          rut: values.rut,
        },
      },
      refetchQueries: [
        {
          query: operations.GET_ALL_GUEST_USERS,
          variables: {
            concessionaireName: name,
          },
        },
      ],
    });
  };

  useEffect(() => {
    if (data && data.inviteUser) {
      message.success('Usuario invitado correctamente', 2);
      history.push('/users');
    }
  }, [data]);

  const renderPageTitle = () => {
    return 'Simulación de Trámites Adicionales';
  };

  return (
    <Row className={styles.Container}>
      <div className={styles.Background} />
      <Col xs={0} sm={8} className={styles.FullHeight}>
        <Row justify="center" align="middle" className={styles.FullSize}>
          <Col xs={16}>
            {logoUrl && (
              <Row
                justify="center"
                align="middle"
                className={styles.MarginBottom}
              >
                <img src={logoUrl} alt={name} width="100" />
              </Row>
            )}
            <Row justify="center" align="middle">
              <h1 className={styles.CenteredText}>{renderPageTitle()}</h1>
            </Row>
          </Col>
        </Row>
      </Col>
      <Col xs={24} sm={16} className={styles.FullHeight}>
        <Row justify="start" align="middle" className={styles.FullHeight}>
          <Col>
            <Form form={form} name="login" onFinish={sendForm}>
              <Row
                justify="start"
                align="middle"
                className={styles.SignUp}
                gutter={12}
              >
                <Col xs={24}>
                  <Row justify="center" align="middle" gutter={12}>
                    <h1 className={styles.Title}>Invitar usuario</h1>
                  </Row>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="name"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: MessageError.emptyFieldsMessages.name,
                      },
                      {
                        whitespace: true,
                        message: MessageError.emptyFieldsMessages.name,
                      },
                    ]}
                  >
                    <Input label="Nombre" placeholder="Nombre" required />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="firstName"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: MessageError.emptyFieldsMessages.lastName,
                      },
                      {
                        whitespace: true,
                        message: MessageError.emptyFieldsMessages.lastName,
                      },
                    ]}
                  >
                    <Input label="Apellido" placeholder="Apellido" required />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="rut"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: MessageError.emptyFieldsMessages.rut,
                      },
                      {
                        whitespace: true,
                        message: MessageError.emptyFieldsMessages.rut,
                      },
                      {
                        validator: rutFormValidator,
                      },
                    ]}
                  >
                    <Input
                      label="Rut"
                      placeholder="Rut"
                      onChange={(e) => handleChangeRut(e.target.value)}
                      required
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="email"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: MessageError.emptyFieldsMessages.email,
                      },
                      {
                        whitespace: true,
                        message: MessageError.emptyFieldsMessages.email,
                      },
                    ]}
                  >
                    <Input
                      label="Email"
                      placeholder="Email@example.com"
                      required
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="dealer"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message: MessageError.emptyFieldsMessages.dealer,
                      },
                      {
                        whitespace: true,
                        message: MessageError.emptyFieldsMessages.dealer,
                      },
                    ]}
                  >
                    <Select
                      label="Sucursal"
                      placeholder="Sucursal"
                      options={mapDealershipOptions}
                      required
                    />
                  </Form.Item>
                </Col>
                <Col xs={12}>
                  <Form.Item
                    name="concessionaireName"
                    className={styles.FormItem}
                    rules={[
                      {
                        required: true,
                        message:
                          MessageError.emptyFieldsMessages.concessionaire,
                      },
                      {
                        whitespace: true,
                        message:
                          MessageError.emptyFieldsMessages.concessionaire,
                      },
                    ]}
                  >
                    <Select
                      label="Concesionaria"
                      placeholder="Concesionaria"
                      options={mapConcessionaireOptions}
                      required
                    />
                  </Form.Item>
                </Col>
                <Row className={styles.ButtonContainer} justify="center">
                  <Button
                    htmlType="submit"
                    size="large"
                    loading={loading}
                    disabled={loading}
                  >
                    Enviar invitación
                  </Button>
                </Row>
              </Row>
            </Form>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default InviteUser;
